import SelectionCell from '@js/components/tables/cells/SelectionCell.vue';
import SelectionHeaderCell from '@js/components/tables/cells/SelectionHeaderCell.vue';
import {
    createColumnHelper,
    getCoreRowModel,
    getExpandedRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useVueTable,
} from '@tanstack/vue-table';
import { computed, h, onMounted, ref } from 'vue';

export function getDefaultPageSize() {
    return 100;
}

export const useDataTable = (options) => {
    const columnHelper = createColumnHelper();

    const fetchUserSession = ref(true);

    const loading = ref(true);

    const globalData = ref({});

    const data = ref([]);

    const count = ref(0);

    const sorting = ref([]);

    const pagination = ref({
        pageIndex: 0,
        pageSize: options.defaultPageSize ?? getDefaultPageSize(),
    });

    const pinning = ref(options.columnPinning ?? {});

    const pageCount = ref(0);

    const globalFilter = ref('');

    const availableFilters = ref([]);

    const filters = ref([]);

    const expanded = ref({});

    const selectionIds = ref([]);

    const allRowsSelected = computed(() => selectionIds.value.includes('all'));

    function getVisibleSelectableIds() {
        const rows = options.isRowSelectable
            ? data.value.filter((row) =>
                  options.isRowSelectable({ original: row }),
              )
            : data.value;

        return rows.map((row) => row[options.selectionIdKey]);
    }

    function selectAll() {
        if (allRowsSelected.value) {
            selectionIds.value = [];
            return;
        }

        selectionIds.value = [...getVisibleSelectableIds(), 'all'];
    }

    function selectAllVisible() {
        const visibleIds = getVisibleSelectableIds();

        const others = selectionIds.value.filter(
            (id) => id !== 'all' && !visibleIds.includes(id),
        );

        const allVisibleSelected =
            visibleIds.length > 0 &&
            visibleIds.every((id) => selectionIds.value.includes(id));

        selectionIds.value = allVisibleSelected
            ? others
            : [...others, ...visibleIds];
    }

    function selectionSize() {
        if (allRowsSelected.value) {
            return count.value;
        }

        return selectionIds.value.filter((id) => id !== 'all').length;
    }

    function setSelectionIds(value) {
        const previousRowCount = selectionIds.value.filter(
            (id) => id !== 'all',
        ).length;
        const newRowCount = value.filter((id) => id !== 'all').length;

        if (allRowsSelected.value && newRowCount < previousRowCount) {
            selectionIds.value = value.filter((id) => id !== 'all');
            return;
        }

        selectionIds.value = value;
    }

    const columns = computed(() => {
        let columns = options.columns;

        if (options.selectionIdKey) {
            columns = [
                columnHelper.display({
                    id: 'selection',
                    header: h(SelectionHeaderCell, {
                        selectAll,
                        selectAllVisible,
                        selectionSize,
                        align: 'center',
                    }),
                    cell: (innerProps) =>
                        h(SelectionCell, {
                            row: innerProps.row,
                            selectionIds: selectionIds.value,
                            setSelectionIds,
                            idKey: options.selectionIdKey,
                            align: 'center',
                            disabled: options.isRowSelectable
                                ? !options.isRowSelectable(innerProps.row)
                                : false,
                        }),
                    width: 0,
                }),
                ...columns,
            ];
        }

        return columns;
    });

    const table = useVueTable({
        get data() {
            return data.value;
        },
        get pageCount() {
            return pageCount.value;
        },
        getSubRows: (row) => {
            return row.children || [];
        },
        getRowCanExpand: options.getRowCanExpand,
        columns: columns.value,
        state: {
            get columnPinning() {
                return pinning.value;
            },
            get pagination() {
                return pagination.value;
            },
            get sorting() {
                return sorting.value;
            },
            get globalFilter() {
                return globalFilter.value;
            },
            get expanded() {
                return expanded.value;
            },
        },
        onColumnPinningChange: (updaterOrValue) => {
            pinning.value =
                typeof updaterOrValue === 'function'
                    ? updaterOrValue(pinning.value)
                    : updaterOrValue;
        },
        onPaginationChange: (updaterOrValue) => {
            pagination.value =
                typeof updaterOrValue === 'function'
                    ? updaterOrValue(pagination.value)
                    : updaterOrValue;

            if (options.url) {
                updateData();
            } else {
                options.updateData();
            }
        },
        onSortingChange: (updaterOrValue) => {
            sorting.value =
                typeof updaterOrValue === 'function'
                    ? updaterOrValue(sorting.value)
                    : updaterOrValue;

            if (options.url) {
                updateData();
            } else {
                options.updateData();
            }
        },
        onGlobalFilterChange: (updaterOrValue) => {
            globalFilter.value =
                typeof updaterOrValue === 'function'
                    ? updaterOrValue(globalFilter.value)
                    : updaterOrValue;

            if (options.url) {
                updateData();
            } else {
                options.updateData();
            }
        },
        onExpandedChange: (updaterOrValue) => {
            expanded.value =
                typeof updaterOrValue === 'function'
                    ? updaterOrValue(expanded.value)
                    : updaterOrValue;
        },
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        initialState: {
            pagination: {
                pageIndex: 0,
                pageSize: options.defaultPageSize ?? getDefaultPageSize(),
            },
            columnVisibility: options.columnVisibility ?? {},
            columnPinning: options.columnPinning ?? {},
        },
        manualPagination: true,
        manualSorting: true,
        manualFiltering: true,
    });

    function setGlobalFilter(value) {
        globalFilter.value = value;
    }

    function setFilters(filters) {
        filters.value = filters;

        if (options.url) {
            updateData();
        } else {
            options.updateData();
        }
    }

    function updateData(callback = null) {
        if (options.url) {
            loading.value = true;

            axios({
                method: 'post',
                url: options.url,
                data: {
                    pageIndex: pagination.value.pageIndex,
                    pageSize: pagination.value.pageSize,
                    sorting: sorting.value,
                    globalFilter: globalFilter.value,
                    filters: filters.value,
                    saveFilters: options.saveFilters ?? false,
                    fetchUserSession: fetchUserSession.value,
                    userSessionKey: options.userSessionKey,
                    data:
                        options.data && options.data.value
                            ? options.data.value
                            : options.data,
                },
                headers: {
                    Accept: 'application/json',
                },
            })
                .then((response) => {
                    data.value = response.data.data;
                    count.value = response.data.count;
                    pagination.value.pageIndex = response.data.pageIndex;
                    pagination.value.pageSize = response.data.pageSize;
                    pageCount.value = response.data.pageCount;
                    sorting.value = response.data.sorting;
                    globalFilter.value = response.data.globalFilter;
                    filters.value = response.data.filters ?? filters.value;
                    availableFilters.value =
                        response.data.availableFilters ??
                        availableFilters.value;
                    globalData.value =
                        response.data.globalData ?? globalData.value;
                })
                .catch((error) => {
                    console.log(error);
                })
                .finally(() => {
                    loading.value = false;

                    if (callback && typeof callback === 'function') {
                        callback();
                    }
                });
        } else {
            throw new Error('No URL provided for data table');
        }
    }

    function selectData() {
        if (options.url) {
            return axios
                .post(options.url, {
                    sorting: sorting.value,
                    globalFilter: globalFilter.value,
                    filters: filters.value,
                    fetchUserSession: fetchUserSession.value,
                    userSessionKey: options.userSessionKey,
                    data:
                        options.data && options.data.value
                            ? options.data.value
                            : options.data,
                    selectionIds: selectionIds.value,
                    select: true,
                })
                .then((response) => {
                    return response.data.data;
                });
        } else {
            throw new Error('No URL provided for data table');
        }
    }

    function exportData(format = 'csv') {
        if (options.exportUrl && options.selectionIdKey) {
            loading.value = true;

            let url = new URL(options.exportUrl);
            url.searchParams.append('format', format);

            axios({
                method: 'post',
                url: url.toString(),
                data: {
                    selectionIds: selectionIds.value,
                    sorting: sorting.value,
                    globalFilter: globalFilter.value,
                    filters: filters.value,
                    selectionIdKey:
                        options.exportSelectionIdKey ?? options.selectionIdKey,
                    data:
                        options.data && options.data.value
                            ? options.data.value
                            : options.data,
                },
                responseType: 'blob',
            })
                .then((response) => {
                    const contentDisposition =
                        response.headers['content-disposition'];

                    if (contentDisposition != null) {
                        let filename = contentDisposition
                            .split(';')[1]
                            .split('=')[1]
                            .replace('"', '')
                            .replace('"', '');
                        filename = decodeURI(filename);

                        const url = window.URL.createObjectURL(
                            new Blob([response.data]),
                        );
                        const link = document.createElement('a');

                        link.href = url;
                        link.setAttribute('download', filename);

                        document.body.appendChild(link);
                        link.click();

                        window.URL.revokeObjectURL(url);
                        link.remove();
                    }
                })
                .catch((error) => {
                    console.log(error);
                })
                .finally(() => {
                    loading.value = false;
                });
        } else {
            throw new Error(
                'No export URL provided for data table or no selection ID key',
            );
        }
    }

    onMounted(() => {
        if (options.baseRequest?.pageSize) {
            pagination.value.pageSize = options.baseRequest.pageSize;
        }

        if (options.baseRequest?.sorting) {
            sorting.value = options.baseRequest.sorting;
        }

        if (options.baseRequest?.globalFilter) {
            globalFilter.value = options.baseRequest.globalFilter;
        }

        if (options.baseRequest?.availableFilters) {
            availableFilters.value = options.baseRequest.availableFilters;
        }

        if (options.baseRequest?.filters) {
            filters.value = options.baseRequest.filters;
        }

        if (options.url) {
            updateData(() => {
                fetchUserSession.value = false;
            });
        } else {
            options.updateData(() => {
                fetchUserSession.value = false;
            });
        }
    });

    return {
        fetchUserSession,
        loading,
        table,
        globalData,
        data,
        count,
        pagination,
        pageCount,
        sorting,
        globalFilter,
        setGlobalFilter,
        availableFilters,
        filters,
        setFilters,
        selectionIds,
        updateData,
        selectData,
        exportData,
    };
};
